/* eslint-disable max-len */
import {
    Alert,
    Box,
    Checkbox,
    FormControl,
    MenuItem,
    Select,
    Typography
} from '@mui/material';
import ActionButtonsFormularioAgricultura from 'components/forms/ActionButtonsFormularioAgricultura';
import { ContainerForm } from 'components/shared/container_form';
import React from 'react';
import { useFormularioSecretariaMunicipalAgriculturaStore } from '../../../../../../stores/forms/formularioAgricultura.store';

interface InstituicaoData {
    selecionada: boolean;
    prioridade: number | null;
}

interface InstituicoesState {
    cooperativas: InstituicaoData;
    embrapa: InstituicaoData;
    empresas_privadas: InstituicaoData;
    ongs: InstituicaoData;
    senar: InstituicaoData;
    universidades_publicas: InstituicaoData;
    empaer: InstituicaoData;
    ifmt: InstituicaoData;
    incra: InstituicaoData;
    sebrae: InstituicaoData;
    universidades_privadas: InstituicaoData;
    nenhuma: InstituicaoData;
}

const INSTITUICOES = [
    { key: 'cooperativas', label: 'Cooperativas' },
    { key: 'embrapa', label: 'Embrapa' },
    { key: 'empresas_privadas', label: 'Empresas Privadas' },
    { key: 'ongs', label: 'ONGs' },
    { key: 'senar', label: 'Senar' },
    { key: 'universidades_publicas', label: 'Universidades Públicas' },
    { key: 'empaer', label: 'Empaer' },
    { key: 'ifmt', label: 'IFMT' },
    { key: 'incra', label: 'Incra' },
    { key: 'sebrae', label: 'Sebrae' },
    { key: 'universidades_privadas', label: 'Universidades Privadas' },
    { key: 'nenhuma', label: 'Nenhuma' },
];

const Step8: React.FC = () => {
    const { formulario, setPartialForm } = useFormularioSecretariaMunicipalAgriculturaStore();

    const initializeState = (): InstituicoesState => {
        const estadoInicial: InstituicoesState = {} as InstituicoesState;

        INSTITUICOES.forEach(({ key }) => {
            estadoInicial[key as keyof InstituicoesState] = {
                selecionada: false,
                prioridade: null
            };
        });

        if (formulario?.ordem_importancia_assistencia_tecnica_outras_instituicoes &&
            Array.isArray(formulario.ordem_importancia_assistencia_tecnica_outras_instituicoes)) {
            try {
                formulario.ordem_importancia_assistencia_tecnica_outras_instituicoes.forEach((item, index) => {
                    const keyTyped = item as keyof InstituicoesState;
                    if (estadoInicial[keyTyped]) {
                        estadoInicial[keyTyped] = {
                            selecionada: true,
                            prioridade: index + 1
                        };
                    }
                });
            } catch {
                // Fallback para estado inicial em caso de erro
            }
        }

        return estadoInicial;
    };

    const [instituicoes, setInstituicoes] = React.useState<InstituicoesState>(initializeState);

    const quantidadeInstituicoesSelecionadas = Object.values(instituicoes)
        .filter(instituicao => instituicao.selecionada).length;

    const salvarNaStore = (dadosInstituicoes: InstituicoesState) => {
        if (dadosInstituicoes.nenhuma.selecionada) {
            setPartialForm('ordem_importancia_assistencia_tecnica_outras_instituicoes', ['nenhuma']);
            return;
        }

        const instituicoesSelecionadasComPrioridade = Object.entries(dadosInstituicoes)
            .filter(([, data]) => data.selecionada && data.prioridade !== null)
            .sort(([, a], [, b]) => (a.prioridade! - b.prioridade!))
            .map(([key]) => key);

        setPartialForm('ordem_importancia_assistencia_tecnica_outras_instituicoes', instituicoesSelecionadasComPrioridade);
    };

    const rebalancearPrioridades = (
        novasInstituicoes: InstituicoesState,
        prioridadeRemovida: number
    ) => {
        Object.keys(novasInstituicoes).forEach(key => {
            const keyTyped = key as keyof InstituicoesState;
            if (keyTyped !== 'nenhuma' && novasInstituicoes[keyTyped].selecionada) {
                const prioridadeAtual = novasInstituicoes[keyTyped].prioridade;
                if (prioridadeAtual !== null && prioridadeAtual > prioridadeRemovida) {
                    novasInstituicoes[keyTyped].prioridade = prioridadeAtual - 1;
                }
            }
        });
    };

    const updateSelecao = (instituicao: keyof InstituicoesState, selecionada: boolean) => {
        const novasInstituicoes = { ...instituicoes };

        if (instituicao === 'nenhuma' && selecionada) {
            INSTITUICOES.forEach(({ key }) => {
                const keyTyped = key as keyof InstituicoesState;
                if (keyTyped !== 'nenhuma') {
                    novasInstituicoes[keyTyped].selecionada = false;
                    novasInstituicoes[keyTyped].prioridade = null;
                }
            });
        } else if (selecionada && instituicoes.nenhuma.selecionada) {
            novasInstituicoes.nenhuma.selecionada = false;
            novasInstituicoes.nenhuma.prioridade = null;
        }

        novasInstituicoes[instituicao].selecionada = selecionada;

        if (!selecionada) {
            const prioridadeRemovida = novasInstituicoes[instituicao].prioridade;
            novasInstituicoes[instituicao].prioridade = null;

            if (prioridadeRemovida !== null) {
                rebalancearPrioridades(novasInstituicoes, prioridadeRemovida);
            }
        } else {
            const instituicoesSelecionadasComPrioridade = Object.entries(novasInstituicoes)
                .filter(([key, data]) => key !== 'nenhuma' && data.selecionada && data.prioridade !== null)
                .length;
            novasInstituicoes[instituicao].prioridade = instituicoesSelecionadasComPrioridade + 1;
        }

        setInstituicoes(novasInstituicoes);
        salvarNaStore(novasInstituicoes);
    };

    const updatePrioridade = (instituicao: keyof InstituicoesState, valor: string) => {
        const novasInstituicoes = { ...instituicoes };

        if (valor === '' || valor === '0') {
            novasInstituicoes[instituicao].prioridade = null;
        } else {
            const num = parseInt(valor);
            if (!isNaN(num) && num >= 1 && num <= 11) {
                const prioridadeAtualInstituicao = novasInstituicoes[instituicao].prioridade;
                const instituicaoComMesmaPrioridade = Object.keys(novasInstituicoes).find(key =>
                    key !== instituicao &&
                    key !== 'nenhuma' &&
                    novasInstituicoes[key as keyof InstituicoesState].selecionada &&
                    novasInstituicoes[key as keyof InstituicoesState].prioridade === num
                );

                if (instituicaoComMesmaPrioridade) {
                    novasInstituicoes[instituicaoComMesmaPrioridade as keyof InstituicoesState].prioridade = prioridadeAtualInstituicao;
                }

                novasInstituicoes[instituicao].prioridade = num;
            }
        }

        setInstituicoes(novasInstituicoes);
        salvarNaStore(novasInstituicoes);
    };

    const nenhumaSelecionada = instituicoes.nenhuma.selecionada;
    const todasInstituicoesSelecionadas = Object.entries(instituicoes)
        .filter(([, data]) => data.selecionada)
        .map(([key]) => key as keyof InstituicoesState);
    const algumaSelecionada = todasInstituicoesSelecionadas.length > 0;
    const podeAvancar = nenhumaSelecionada || (algumaSelecionada);

    return (
        <ContainerForm title='Assistência Técnica'>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }} gap={4} display={'flex'} flexDirection={'column'}>

                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Instrução de preenchimento:</strong><br />
                        Marque 1 para o mais importante, 2 para o segundo mais importante, e assim por diante. Se você selecionar um número já usado, as prioridades serão trocadas automaticamente.
                    </Typography>
                </Alert>

                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                    <span style={{ color: 'red', fontSize: '18px', marginRight: '8px' }}>*</span>
                    Os agricultores familiares do município receberam assistência técnica de outras instituições?
                </Typography>

                <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                        {INSTITUICOES.map((instituicao) => (
                            <Box
                                key={instituicao.key}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    {
                                        instituicao.key == 'nenhuma' ?
                                            <>
                                                <Checkbox
                                                    checked={nenhumaSelecionada}
                                                    onChange={(e) => updateSelecao(instituicao.key as keyof InstituicoesState, true)}
                                                />
                                            </>

                                            :
                                            <>
                                                <Checkbox
                                                    checked={instituicoes[instituicao.key as keyof InstituicoesState].selecionada}
                                                    onChange={(e) => updateSelecao(instituicao.key as keyof InstituicoesState, e.target.checked)}
                                                />
                                            </>
                                    }

                                    <Typography>{instituicao.label}</Typography>
                                </Box>

                                <FormControl>
                                    {instituicao.key !== 'nenhuma' && (
                                        <Select
                                            value={instituicoes[instituicao.key as keyof InstituicoesState].prioridade?.toString() || ''}
                                            sx={{ width: '100px' }}
                                            onChange={(e) => updatePrioridade(instituicao.key as keyof InstituicoesState, e.target.value)}
                                            disabled={!instituicoes[instituicao.key as keyof InstituicoesState].selecionada}
                                        >
                                            {Array.from({ length: quantidadeInstituicoesSelecionadas }, (_, index) => (
                                                <MenuItem key={index} value={index + 1}>{index + 1}</MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                </FormControl>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        <strong>Observações:</strong><br />
                        • Primeiro selecione as instituições na Lista 1<br />
                        • Depois defina as prioridades na Lista 2 (números de 1 a 11)<br />
                        • Número 1 = maior prioridade<br />
                        • Se selecionar &quot;Nenhuma&quot;, as demais opções serão desabilitadas<br />
                        • As prioridades são trocadas automaticamente se você selecionar um número já usado<br />
                        • Todas as instituições selecionadas devem ter prioridade definida
                    </Typography>
                </Alert>
            </Box>

            <ActionButtonsFormularioAgricultura disabled={!podeAvancar} />
        </ContainerForm>
    );
};

export default Step8; 